import { defineChatViewActions } from './actionsContext'
import type { ChatViewThis } from './actionsContext'
import { useChatStore as useChatStoreCore } from '../chatStore'
import { hasRoomMessagesCache } from '../core/messageRoomCache'
import { hasLocalMatrixRoomTimeline } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { handleChatError } from '../../utils'
import { isGroupLikeChatType } from '../../constants/chat'
import { buildTopicsUnreadDebugBreakdown, isTopicsModeChat } from '@/modules/chat-matrix-fast/utils/topics'
import { setChatDebugLaneFromChat, summarizeTopicForLog, topicsDebugLog, debugConsoleError } from '../../utils/chatDebugLog'
import type { ChatContact } from '../../types'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'

interface OpenDirectChatOptions {
  silent?: boolean
  hints?: {
    phone?: string
    fullName?: string
    avatar?: string | null
    matrixUserId?: string
  }
  ensureListTab?: boolean
}

/** Один find→create→open: иначе sync/moveToTop повторялись в трёх entry-point'ах. */
async function openFoundOrCreatedDirectChat(
  view: ChatViewThis,
  userId: string,
  options: OpenDirectChatOptions = {},
): Promise<void> {
  const chatStore = useChatStoreCore()
  const chatExistedBefore = Boolean(await chatStore.findExistingDirectChat(userId))
  const dialog = await chatStore.findOrCreateDirectChat(
    userId,
    options.silent ?? false,
    options.hints,
  )

  if (options.ensureListTab) {
    await view.ensureChatListTabForChat(dialog)
  }

  if (!chatExistedBefore) {
    view.syncWithCoreStore()
    setTimeout(() => {
      view.moveChatToTop(dialog.id)
    }, 0)
  }

  await view.openExistingChat(dialog, dialog.id)
}

export const openChatActions = defineChatViewActions({
    async openDirectChatWithEmployee(contact: ChatContact): Promise<void> {
      const chatStore = useChatStoreCore()
      const employeeId = contact.employeeId ?? String(contact.id)

      await openFoundOrCreatedDirectChat(this, employeeId, {
        silent: true,
        ensureListTab: true,
        hints: {
          phone: contact.phoneNumber,
          fullName: contact.fullName,
          avatar: contact.avatar || null,
          matrixUserId: contact.matrixUserId,
        },
      })

      if (chatStore.searchResults !== null) {
        chatStore.searchResults = null
        await this.fetchChatsAndContacts('')
      }
    },

    async getChat(
      userId: ChatContact['id'],
      options?: { preferTopicsList?: boolean },
    ) {
      topicsDebugLog('view · getChat · start', { userId })
      const chatStore = useChatStoreCore()

      const searchContact = this.contacts.find(contact => contact.id === userId)
      if (searchContact) {
        await this.openDirectChatWithEmployee(searchContact)
        return
      }

      const isNumericId = typeof userId === 'number' || (typeof userId === 'string' && !isNaN(Number(userId)) && userId.trim() !== '')

      let cachedChat: IChat | undefined

      if (isNumericId) {
        cachedChat = chatStore.chats.find(c => c.id === userId)
      } else {
        cachedChat = await chatStore.findExistingDirectChat(String(userId))
      }

      if (cachedChat) {
        setChatDebugLaneFromChat(cachedChat)
        await this.ensureChatListTabForChat(cachedChat)

        if (isGroupLikeChatType(cachedChat.type) || cachedChat.type === 'event') {
          topicsDebugLog('view · getChat · openExistingChat (cached group/event)', {
            userId,
            chatId: cachedChat.id,
            discussionMode: cachedChat.discussion_mode,
          })
          await this.openExistingChat(cachedChat, userId, options)

          return
        }

        await this.openExistingChat(cachedChat, userId, options)

        return
      }

      if (isNumericId) {
        const chat = await this.fetchChatById(userId)

        if (chat) {
          await this.ensureChatListTabForChat(chat)
          await this.openExistingChat(chat, userId, options)

          return
        }
      }

      if (typeof userId === 'string' && !isNumericId) {
        try {
          await openFoundOrCreatedDirectChat(this, userId)
          return
        } catch (error) {
          handleChatError(error, 'Create or find dialog')
        }
      }

      await this.createNewChat(userId)
    },


    async openExistingChat(
      chat: IChat,
      userId: number | string,
      options?: { preferTopicsList?: boolean },
    ): Promise<void> {
      const isTopicsGroup = isTopicsModeChat(chat)
      const chatStore = useChatStoreCore()
      const isSameChatAlreadyOpen = this.activeChat?.chat?.id === chat.id
        && chatStore.currentChat?.id === chat.id
        && !chatStore.activeTopic
        && !chatStore.showTopicsList
        && !isTopicsGroup

      topicsDebugLog('view · openExistingChat · start', {
        userId,
        chatId: chat.id,
        isTopicsGroup,
        topicsCount: chat.topics?.length ?? 0,
        isSameChatAlreadyOpen,
      })

      // Уже открыт — не сбрасываем activeChat/аватар.
      if (isSameChatAlreadyOpen) {
        void chatStore.markChatAsRead(chat.id)
        topicsDebugLog('view · openExistingChat · skip (already open)', {
          chatId: chat.id,
          uiMessagesCount: chatStore.messages.length,
        })
        if (chatStore.messages.length === 0) {
          void chatStore.fetchMessages(chat.id)
        }
        return
      }

      const roomId = chat.matrix_room_id?.trim()
        || chatStore.chats.find(item => item.id === chat.id)?.matrix_room_id?.trim()
      const hasCachedTimeline = !isTopicsGroup && (
        hasRoomMessagesCache(chatStore, roomId)
        || hasLocalMatrixRoomTimeline(roomId)
      )

      this.isLoadingChat = !isTopicsGroup && !hasCachedTimeline

      this.updateActiveChat(chat, userId)

      const expectedChatId = chat.id

      if (!isTopicsGroup) {
        void chatStore.markChatAsRead(chat.id)

        topicsDebugLog('view · openExistingChat · markChatAsRead', {
          userId,
          chatId: chat.id,
          isTopicsGroup,
          unseenMsgsAfter: this.chatsContacts.find(c => c.id === userId)?.chat?.unseenMsgs ?? null,
        })
      } else {
        topicsDebugLog('view · openExistingChat · skip markChatAsRead (topics group)', {
          userId,
          chatId: chat.id,
          unreadBreakdown: buildTopicsUnreadDebugBreakdown(chat),
        })
      }

      try {
        await this.openChatSession(chat, options)

        if (this.activeChat?.chat?.id !== expectedChatId) {
          this.isLoadingChat = false
          topicsDebugLog('view · openExistingChat · stale, skip apply', {
            chatId: chat.id,
            uiActiveChatId: this.activeChat?.chat?.id ?? null,
          })
          return
        }

        if (isTopicsGroup || isTopicsModeChat(chatStore.currentChat)) {
          this.isLoadingChat = false
          topicsDebugLog('view · openExistingChat · done (topics)', {
            chatId: chat.id,
            uiActiveChatId: this.activeChat?.chat?.id ?? null,
            uiMessagesCount: chatStore.messages.length,
            coreActiveTopic: summarizeTopicForLog(chatStore.activeTopic),
            coreShowTopicsList: chatStore.showTopicsList,
          })
          return
        }

        this.isLoadingChat = false
      } catch (error) {
        this.isLoadingChat = false
        handleChatError(error, 'Open chat session')
      }
    },


    async createNewChat(userId: number | string): Promise<void> {
      if (typeof userId !== 'string') {
        return
      }

      try {
        await openFoundOrCreatedDirectChat(this, userId)
      } catch (error) {
        handleChatError(error, 'Create or find dialog')
      }
    },


    async fetchChatById(userId: number | string): Promise<IChat | null> {
      const chatStore = useChatStoreCore()

      const numericId = typeof userId === 'number' ? userId : Number(userId)
      if (isNaN(numericId) || numericId <= 0) {
        return null
      }

      try {
        const cachedChat = chatStore.chats.find(c => c.id === numericId)
        const cachedLastMessage = cachedChat?.last_message

        const detailedChat = await chatStore.fetchChat(numericId)

        if (cachedLastMessage && !detailedChat.last_message) {
          detailedChat.last_message = cachedLastMessage
        }

        const chatIndex = chatStore.chats.findIndex(c => c.id === numericId)
        if (chatIndex !== -1) {
          chatStore.chats[chatIndex] = detailedChat
        }

        return detailedChat
      } catch (error) {
        debugConsoleError('[chatViewStore] fetchChatById - ошибка загрузки:', error)

        const chat = chatStore.chats.find(c => c.id === numericId)

        return chat || null
      }
    },


    async openChatSession(
      chat: IChat,
      options?: { preferTopicsList?: boolean },
    ): Promise<void> {
      const chatStore = useChatStoreCore()

      this.clearLastMessageReaders()

      await chatStore.openChat(chat, options)
    },

})
